import { Insurance } from "assets/icons/Insurance";
import { UserRole } from "constants/userRole";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

import { CustomNavLink } from "./components/CustomNavLink/CustomNavLink";

const Container = styled.div`
  width: 96px;

  top: var(--navbar-height);
  position: fixed;
  bottom: 0;

  background-color: var(--color-white);
  border-right: 1px solid var(--color-black-1);

  gap: 30px;

  display: flex;
  align-items: stretch;
  flex-direction: column;

  padding: var(--space-md) var(--space-sm);
`;

const Item = styled(NavLink)`
  all: unset;

  width: 100%;
  cursor: pointer;
  box-sizing: border-box;

  border-radius: 12px;
  padding: 10px 4px;
  gap: 8px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;

  font-size: 12px;
  font-weight: 500;
  color: var(--color-black-9, #344054);

  span {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  i,
  svg {
    font-size: 18px;
    flex-shrink: 0;
    color: var(--color-black-9, #344054);
  }

  :hover {
    background: var(--primary-10);
    color: var(--color-purple);

    i,
    svg {
      color: var(--color-purple);
    }
  }

  &.active {
    background: var(--primary-10);
    color: var(--color-purple);
    font-weight: 600;

    i,
    svg {
      color: var(--color-purple);
    }
  }
`;

export function Sidebar(): JSX.Element {
  const { scope } = useSelector(userSelector);

  return (
    <Container>
      <div className="flex flex-column">
        {scope === UserRole.SUPER_ADMIN && (
          <>
            <Item data-testid="sidebar-reports-link" to="/reports">
              <i className="pi pi-chart-bar" />
              <span>Reports</span>
            </Item>

            <CustomNavLink
              to="/hospitals"
              data-testid="sidebar-hospitals-link"
              matchLinks={["/hospitals/*", "/orders/*"]}
            >
              <i className="pi pi-building" />
              <span>Hospitals</span>
            </CustomNavLink>
            <Item to="/patients">
              <i className="pi pi-users" />
              <span>Patients</span>
            </Item>

            <Item to="/fee-schedule" data-testid="sidebar-fee-schedule-link">
              <i className="pi pi-money-bill" />
              <span>
                Fee
                <br />
                Schedule
              </span>
            </Item>

            <Item to="/users">
              <i className="pi pi-user-edit" />
              <span>Users</span>
            </Item>
            <Item to="/customize" data-test-id="sidebar-customize-link">
              <i className="pi pi-sliders-h" />
              <span>Customize</span>
            </Item>
            <Item to="/payer-rules" data-test-id="sidebar-payer-rules-link">
              <Insurance width={18} height={18} />
              <span>
                Payer
                <br />
                Rules
              </span>
            </Item>
            <Item
              to="/revenue-dashboard"
              data-test-id="sidebar-revenue-dashboard-link"
            >
              <i className="pi pi-dollar" />
              <span>Revenue</span>
            </Item>
            <Item to="/chatbot" data-test-id="sidebar-chatbot-link">
              <i className="pi pi-android" />
              <span>Chatbot</span>
            </Item>
            <Item to="/hst-sync" data-test-id="sidebar-hst-sync-link">
              <i className="pi pi-sync" />
              <span>HST sync</span>
            </Item>
            {/*
              HST CSV imports: every import is recorded as a batch, with the uploaded files and a
              SuperAdmin-only undo for an import that went into the wrong account.
            */}
            <Item to="/integrations" data-test-id="sidebar-integrations-link">
              <i className="pi pi-share-alt" />
              <span>Integrations</span>
            </Item>
            {/*
              CLE-2792 — the settings behind every eligibility check. Not a payer's page and not an
              account's, so it gets its own entry rather than hiding under either.

              CLE-2820 — super admin, like every entry in this block. It used to be addressed to two
              named people while the engine was mid-cutover; a screen that exists for a role but is
              hidden from most of that role is a permission nobody can grant, so the role is the gate
              now. `/eligibility` is role-protected on the route and every endpoint behind it
              requires SuperAdmin, so this entry never leads anywhere the user cannot go.
            */}
            <Item to="/eligibility" data-test-id="sidebar-eligibility-link">
              <i className="pi pi-book" />
              <span>Eligibility</span>
            </Item>
            {/*
              CLE-2811 — the AI contract-modeling tool. It shipped with a route and no way to reach
              it, so the only people who could open it were the ones who had been sent the URL.
              Last in the list on purpose: it is a super-admin tool, not a daily screen.
            */}
            <Item
              to="/contract-model-drafts"
              data-test-id="sidebar-contract-model-drafts-link"
            >
              <i className="pi pi-file-edit" />
              <span>
                Contract
                <br />
                AI Tool
              </span>
            </Item>
          </>
        )}
      </div>
    </Container>
  );
}
